import { HttpContext, HttpContextToken } from '@angular/common/http';

/**
 * Per-request HTTP timeout budget — the token and the values, with no behaviour.
 *
 * ## Why this is its own module
 *
 * Both the interceptor that enforces the budget (`core/interceptors/timeout.interceptor.ts`)
 * and the API classes that opt out of or extend it (`core/apis/*`) need these
 * symbols. Defining them in the interceptor made the API layer import from the
 * interceptor layer — backwards, and a cycle waiting to happen:
 * `auth.interceptor.ts` imports the `core/apis` barrel, so an API importing the
 * `core/interceptors` barrel closes the loop and yields an undefined binding at
 * module-init, which is painful to diagnose.
 *
 * This module imports nothing but Angular, so it is a leaf both layers can
 * depend on safely. Keep it that way: no services, no error copy, no logic.
 */

/**
 * Default budget for a single HTTP request.
 *
 * Generous on purpose: this is a hang guard, not a latency policy. Nothing
 * healthy takes a minute, so a request that does is stuck, and the point is
 * that the UI stops waiting on it — not that slow-but-working requests get cut
 * off. `config.service.ts` and `account.service.ts` impose *shorter* budgets of
 * their own and are unaffected, since the inner timeout always fires first.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 60_000;

/**
 * Budget for calls that legitimately run long: export generation (heavy
 * server-side work) and the payment-reference upload (base64 inflates the
 * payload ~33%). Still finite — an unbounded value would reintroduce the exact
 * hang this mechanism exists to prevent, on the request most likely to stall on
 * a poor connection.
 */
export const EXTENDED_REQUEST_TIMEOUT_MS = 120_000;

/**
 * Disables the timeout for a request.
 *
 * Reserved for calls where a client-side abort is *more* dangerous than an
 * indefinite wait — specifically the Gozem Money execute step. See the call
 * sites in `send-api.ts`, `topup-api.ts` and `withdrawal-api.ts` for the full
 * reasoning; do not apply it anywhere else without the same analysis.
 */
export const NO_REQUEST_TIMEOUT = 0;

/**
 * The budget, in milliseconds, that `timeoutInterceptor` will enforce for this
 * request. Defaults to {@link DEFAULT_REQUEST_TIMEOUT_MS}.
 *
 * A context token rather than a URL match, because URLs do not identify calls
 * finely enough here: `addPaymentReference` posts to
 * `/api/gozem_money/v1/update_payment`, and so do the small
 * `updatePaymentCategory` and `updatePaymentNote` calls. Matching on that URL
 * would silently hand the extended budget to two requests that should keep the
 * default one.
 *
 * Any value that is not finite and positive — {@link NO_REQUEST_TIMEOUT},
 * negative, `NaN`, `Infinity` — disables the timeout.
 */
export const REQUEST_TIMEOUT_BUDGET = new HttpContextToken<number>(
  () => DEFAULT_REQUEST_TIMEOUT_MS,
);

/**
 * Context granting {@link EXTENDED_REQUEST_TIMEOUT_MS} to a long-running call.
 *
 * Built fresh per call: `HttpContext` is mutable, so a shared instance would let
 * one request's state leak into another's.
 */
export function extendedTimeoutContext(): HttpContext {
  return new HttpContext().set(REQUEST_TIMEOUT_BUDGET, EXTENDED_REQUEST_TIMEOUT_MS);
}

/**
 * Context disabling the timeout, for the Gozem Money **execute** step only —
 * `init_transfer`, `init_psp_transfer`, `init_psp_topup`, `init_psp_withdraw`.
 *
 * ## Why these calls must never be timed out
 *
 * A client-side timeout aborts *our wait*, not the *server's work*. These four
 * endpoints are the step that actually moves money, and they call out to a PSP or
 * mobile-money operator — exactly the dependency that goes slow. So a request cut
 * off at 60s may well have succeeded on the backend a moment later, leaving the
 * outcome genuinely unknown to us.
 *
 * Surfacing a timeout there would show copy that invites a retry, and this app
 * sends no idempotency key on any request. A user following that instruction
 * would send the money a second time. An indefinite spinner is recoverable; a
 * duplicate transfer is not, so the spinner is the better failure.
 *
 * Deliberately scoped to the execute step. The preceding `auth_txn` call only
 * validates and computes fees, so it keeps the default budget and the rest of the
 * wallet flow stays protected.
 *
 * Revisit only with confirmation from the Gozem Money team that these endpoints
 * deduplicate (withdrawal already threads a pre-created `withdrawal_id`, which
 * may already provide it). Until then, do not "fix" this back.
 */
export function noTimeoutContext(): HttpContext {
  return new HttpContext().set(REQUEST_TIMEOUT_BUDGET, NO_REQUEST_TIMEOUT);
}
